import {Tooltip} from '@mantine/core';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import Banner from 'buffers/Banner';
import DataConnector from 'connect/DataConnector';
import {AlignmentPolymerType, AaSequenceAlignment} from 'constants/AlignmentConstants';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import AlignmentPDFDownloadModal from 'molbio/AlignmentPDFDownloadModal';
import {generateRawSequencesFromAaSequenceAlignment} from 'molbio/AlignmentPDFHelpers/GenerateRawSequencesFromAlignment';
import {getAntibodyVisibilitySettings} from 'molbio/antibody-engineering/visibilityHelpers';
import DisplayOptions from 'settings/visibility-options/DisplayOptions';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ModalAnchor from 'ui-library/ModalAnchor';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {textToDataURI} from 'util/TextToBlobUtil';

import AaSequenceStore from '../AaSequenceStore';
import {fastaFormat} from './AaSequenceAlignmentUtil';
import AaSequenceRealignModal from './AaSequenceRealignModal';
import ExportPNGModal from './ExportPNGModal';

interface ConnectedProps {
  allowFeatureEdits?: boolean;
}

class AaSequenceAlignmentToolbarConnector extends DataConnector<
  AaSequenceAlignmentToolbarProps,
  ConnectedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore];

  enhanceProps(): ConnectedProps {
    const aaSequence = this.aaSequenceStore().getAaSequenceById(this.props.alignment.proteinId);
    return {
      allowFeatureEdits: aaSequence && aaSequence.permissions.polymerFeaturesEditable!,
    };
  }
}

interface AaSequenceAlignmentToolbarProps {
  visibilityOptions: VisibilityOptions;
  alignment: AaSequenceAlignment;
  renderAlignmentSVG: () => Promise<SVGSVGElement>;
  selection: {
    start: number;
    end: number | null;
  };
}

interface State {
  isExportModalOpen: boolean;
  isRealignModalOpen: boolean;
}

export class AaSequenceAlignmentToolbar extends React.Component<
  AaSequenceAlignmentToolbarProps & ConnectedProps,
  State
> {
  override state: State = {
    isExportModalOpen: false,
    isRealignModalOpen: false,
  };

  _cachedFastaURI?: string;
  _cachedAlignment?: AaSequenceAlignment;

  getCachedFastaURI(): string | undefined {
    if (this._cachedAlignment !== this.props.alignment) {
      this._cachedAlignment = this.props.alignment;
      this._cachedFastaURI = textToDataURI(fastaFormat(this.props.alignment));
    }
    return this._cachedFastaURI;
  }

  _handleRealign = (): void => {
    this.setState({isRealignModalOpen: true});
  };

  _handleCloseRealignModal = (): void => {
    this.setState({isRealignModalOpen: false});
  };

  _handleExportPNG = (): void => {
    this.setState({isExportModalOpen: true});
  };

  _handleCloseExportModal = (): void => {
    this.setState({isExportModalOpen: false});
  };

  _handleExportPDF = async (): Promise<void> => {
    const rawAlignmentData = await generateRawSequencesFromAaSequenceAlignment(
      this.props.alignment,
      this.props.visibilityOptions
    );
    analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_FORMATTED_PDF_MODAL, {
      residueType: AlignmentPolymerType.AA,
    });
    legacyRunModal(AlignmentPDFDownloadModal, {
      rawAlignmentData,
      alignmentPolymerType: AlignmentPolymerType.AA,
      selection: this.props.selection,
      alignmentName: this.props.alignment.name,
      cdrDefinition: getAntibodyVisibilitySettings(this.props.visibilityOptions)[1],
    });
  };

  override render(): JSX.Element {
    const alignmentUrl = `${window.location.href}?alignment=${this.props.alignment.id}`;
    return (
      <div>
        <ModalAnchor open={this.state.isExportModalOpen}>
          <ExportPNGModal
            title="Export AA sequence alignment"
            downloadName={`${this.props.alignment.name}.png`}
            onRequestClose={this._handleCloseExportModal}
            renderSVG={this.props.renderAlignmentSVG}
          />
        </ModalAnchor>
        <ModalAnchor open={this.state.isRealignModalOpen}>
          <AaSequenceRealignModal
            alignment={this.props.alignment}
            onRequestClose={this._handleCloseRealignModal}
          />
        </ModalAnchor>
        <div className="navbar nav buffer-options-container">
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={!this.props.allowFeatureEdits}
            onClick={this._handleRealign}
            size={ButtonSize.MINI}
            text="Realign"
          />
          <div className="buffer-options-item--bannerContainer buffer-options-item--aaSequenceBannerContainer">
            <Banner message="AA sequence alignments are read-only." />
          </div>
          <div className="buttonGroup">
            <Tooltip label="Export" withinPortal={true}>
              <div className="buttonGroup">
                <Dropdown
                  menu={
                    <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
                      <Option download={`${this.props.alignment.name}.fasta`} href={this.getCachedFastaURI()}>
                        FASTA
                      </Option>
                      <Option onSelect={this._handleExportPNG}>PNG</Option>
                      <Option onSelect={this._handleExportPDF}>PDF</Option>
                      <Option
                        onSelect={() => {
                          copyToClipboard(alignmentUrl, {
                            showToastOnSuccess: true,
                            onSuccess: () => {
                              analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.COPIED_ALIGNMENT_URL, {
                                residueType: AlignmentPolymerType.AA,
                              });
                            },
                          });
                        }}
                      >
                        <div>Copy Link</div>
                      </Option>
                    </Menu>
                  }
                >
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    icon="export"
                    isDropdown={true}
                    size={ButtonSize.MINI}
                  />
                </Dropdown>
              </div>
            </Tooltip>
            <DisplayOptions dropLeft={true} options={this.props.visibilityOptions} />
          </div>
        </div>
      </div>
    );
  }
}

export default AaSequenceAlignmentToolbarConnector.connect(AaSequenceAlignmentToolbar);
